import { getAutoWhereBuilder } from './auto-where-builders';
import { isFilterValueEmpty, normalizeValueForOperator } from './operators';
import type {
    CustomWhereBuilder,
    Filter,
    FilterConfig,
    PrismaWhereClause,
    TextSearchFieldConfig,
    TypedFieldConfig,
} from './types';

/**
 * Build a Prisma where clause from filters
 */
export function buildWhereClause<TWhere>(filters: Filter[], config: FilterConfig<TWhere>): TWhere {
    if (filters.length === 0) {
        return {} as TWhere;
    }

    const conditions: PrismaWhereClause<TWhere>[] = [];

    for (const filter of filters) {
        // Find the filter configuration
        const fieldConfig = config.filters.find((f) => f.id === filter.filterId);
        if (!fieldConfig) {
            console.warn(`Filter configuration not found for: ${filter.filterId}`);
            continue;
        }

        const normalizedValue = normalizeValueForOperator(filter.value, filter.operator, fieldConfig.type ?? 'text');

        // Skip filters that contribute no condition (empty value for a value-requiring operator).
        if (isFilterValueEmpty(filter.operator, normalizedValue)) {
            continue;
        }

        // Apply value transformation if provided
        let transformedValue = normalizedValue;
        if (fieldConfig.transform && normalizedValue !== null && normalizedValue !== undefined) {
            transformedValue = fieldConfig.transform(normalizedValue);
            // If transform returns null/undefined, skip this filter
            if (transformedValue === null || transformedValue === undefined) {
                continue;
            }
        }

        // Check if field has custom where builder or dbField
        let condition: PrismaWhereClause<TWhere> | null | undefined;

        if (fieldConfig.customWhereBuilder) {
            // Use custom where builder
            // The UI enforces valid operators per field type, so the cast is safe
            const builder = fieldConfig.customWhereBuilder as CustomWhereBuilder<TWhere>;
            condition = builder(filter.operator, transformedValue, filter.filterId);
        } else if ('dbField' in fieldConfig && fieldConfig.dbField && 'type' in fieldConfig && fieldConfig.type) {
            // Use automatic where builder based on field type
            const typedConfig = fieldConfig as TypedFieldConfig<any, TWhere, any> | TextSearchFieldConfig<TWhere>;
            if (typedConfig.type === 'textSearch') {
                // textSearch should always have a customWhereBuilder
                console.warn(`textSearch field ${filter.filterId} should have a customWhereBuilder`);
                continue;
            } else if (typedConfig.dbField) {
                const autoBuilder = getAutoWhereBuilder(typedConfig.type, typedConfig.dbField);

                if (autoBuilder) {
                    condition = autoBuilder(filter.operator, transformedValue);
                } else {
                    console.warn(`No automatic where builder for field type: ${typedConfig.type}`);
                    continue;
                }
            }
        } else {
            // For filters without custom where builder or dbField
            console.warn(`Filter ${filter.filterId} needs either a customWhereBuilder or dbField`);
            continue;
        }

        if (condition && Object.keys(condition).length > 0) {
            conditions.push(condition);
        }
    }

    // If no valid conditions, return empty where
    if (conditions.length === 0) {
        return {} as TWhere;
    }

    // Always return conditions wrapped in AND array for consistency
    return { AND: conditions } as TWhere;
}

/**
 * Merge multiple where clauses
 */
export function mergeWhereClauses<TWhere>(...clauses: TWhere[]): TWhere {
    const nonEmptyClauses = clauses.filter(
        (clause) => clause && typeof clause === 'object' && Object.keys(clause).length > 0,
    );

    if (nonEmptyClauses.length === 0) {
        return {} as TWhere;
    }

    if (nonEmptyClauses.length === 1) {
        return nonEmptyClauses[0];
    }

    return { AND: nonEmptyClauses } as TWhere;
}

/**
 * Check if a where clause is empty
 */
export function isWhereClauseEmpty<TWhere>(where: TWhere): boolean {
    if (!where || typeof where !== 'object') {
        return true;
    }

    const keys = Object.keys(where);

    // Check for empty object
    if (keys.length === 0) {
        return true;
    }

    // Check for empty AND/OR arrays
    if (keys.length === 1) {
        const key = keys[0];
        if (key === 'AND' || key === 'OR') {
            const value = (where as Record<string, unknown>)[key];
            return !Array.isArray(value) || value.length === 0;
        }
    }

    return false;
}
